import React from 'react'
import { useForm, router, usePage } from '@inertiajs/react'
import Layout from '../../components/Layout'
import Breadcrumb from '../../components/Breadcrumb'
import LoteMapLibre from '../../components/maps/LoteMapLibre'
import BooleanField from '../../components/forms/BooleanField'
import ErrorSummary from '../../components/forms/ErrorSummary'
import VarejoForm from '../varejos/VarejoForm'
import VarejoInfo from '../varejos/VarejoInfo'

function InfoItem({ label, value }) {
  let displayValue = value
  if (typeof value === 'boolean') {
    displayValue = value ? 'Sim' : 'Não'
  } else if (value === null || value === undefined || value === '') {
    displayValue = '-'
  }

  return (
    <div className="flex gap-2 py-1">
      <span className="font-medium text-gray-700">{label}:</span>
      <span className="text-gray-600">{displayValue}</span>
    </div>
  )
}

function InlineVarejoEditor({ varejo, task, errors }) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="font-medium text-gray-900">
          {varejo.nome || `Varejo #${varejo.id}`}
        </span>
        <span className="text-sm text-brand-light">
          {isOpen ? 'Fechar' : 'Editar'}
        </span>
      </button>

      {isOpen && (
        <div className="p-4 border-t border-gray-200 bg-white">
          <VarejoForm
            varejo={varejo}
            task={task}
            errors={errors}
          />
        </div>
      )}
    </div>
  )
}

export default function LoteShow({
  lote,
  quadra,
  polo,
  varejos = [],
  assigned = [],
  canEdit = false,
  canRevise = false,
  canAddVarejo = false,
  isAdmin = false
}) {
  const { errors } = usePage().props
  const task = canEdit ? 'input_data' : canRevise ? 'revise' : null

  const { data, setData, patch, processing } = useForm({
    task: task,
    lote: {
      belongs_to_polo: lote.belongs_to_polo,
    },
  })

  const [addingVarejo, setAddingVarejo] = React.useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/lotes/${lote.id}`, { forceFormData: true })
  }

  const handleAddVarejo = () => {
    setAddingVarejo(true)
    router.post('/varejos', { varejo: { lote_id: lote.id } }, {
      forceFormData: true,
      onFinish: () => setAddingVarejo(false),
    })
  }

  const loteErrors = errors?.lote || errors || {}

  // Build breadcrumb items
  const breadcrumbItems = []
  if (polo) {
    breadcrumbItems.push({ label: 'Polo ' + polo.nome, href: `/polos/${polo.id}` })
  }
  if (quadra) {
    breadcrumbItems.push({ label: 'Quadra', href: `/quadras/${quadra.id}` })
  }

  return (
    <Layout>
      <div className="py-8">
        {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}

        <h1 className="text-2xl font-bold text-brand-dark mb-4 font-heading">Lote</h1>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Info Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-brand-dark mb-4">Informações Atuais</h2>
            <div className="mb-6">
              {task && polo ? (
                <form onSubmit={handleSubmit}>
                  <ErrorSummary errors={errors?.lote || errors} />
                  <div className="space-y-1 mb-4">
                    <InfoItem label="ID" value={lote.id} />
                    <InfoItem label="Polo" value={polo?.nome} />
                    <InfoItem label="Quadra" value={quadra?.id} />
                  </div>

                  <BooleanField
                    label="Este lote pertence ao polo?"
                    name="lote[belongs_to_polo]"
                    value={data.lote.belongs_to_polo}
                    onChange={(val) => setData('lote', { ...data.lote, belongs_to_polo: val })}
                    error={loteErrors.belongs_to_polo}
                  />

                  <button
                    type="submit"
                    disabled={processing}
                    className="mt-2 px-6 py-2 bg-brand-dark hover:bg-brand-light text-white font-medium rounded-md transition-colors disabled:opacity-50"
                  >
                    {processing ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </form>
              ) : (
                <div className="space-y-1">
                  <InfoItem label="ID" value={lote.id} />
                  <InfoItem label="Polo" value={polo?.nome} />
                  <InfoItem label="Quadra" value={quadra?.id} />
                  <InfoItem label="Pertence ao polo" value={lote.belongs_to_polo} />
                </div>
              )}
            </div>

            {/* Varejos List */}
            <div>
              <h3 className="font-semibold text-brand-dark mb-2">Seus varejos</h3>
              {varejos.length === 0 ? (
                <p className="text-gray-500 italic">Nenhum varejo cadastrado</p>
              ) : task ? (
                <div className="space-y-4">
                  {varejos.map(varejo => (
                    <InlineVarejoEditor
                      key={varejo.id}
                      varejo={varejo}
                      task={task}
                      errors={errors?.varejo || {}}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {varejos.map(varejo => (
                    <VarejoInfo key={varejo.id} varejo={varejo} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Map Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-brand-dark mb-4">Mapa</h2>
            <LoteMapLibre loteId={lote.id} className="h-[400px]" />
          </div>
        </div>

        {/* Add Varejo Button */}
        {canAddVarejo && (
          <div className="mb-6">
            <button
              type="button"
              onClick={handleAddVarejo}
              disabled={addingVarejo}
              className="px-4 py-2 bg-brand-dark hover:bg-brand-light text-white font-medium rounded-md transition-colors disabled:opacity-50"
            >
              {addingVarejo ? 'Adicionando...' : 'Adicionar Varejo'}
            </button>
          </div>
        )}

        {/* Responsibles Section - Visible to all */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-brand-dark mb-2">Responsáveis pelo lote:</h2>
          {isAdmin && (
            <p className="text-gray-500 text-sm italic mb-4">(Altere-os na página do polo)</p>
          )}
          {assigned.length === 0 ? (
            <p className="text-gray-500 italic ml-4">Ainda não há responsáveis pelo lote</p>
          ) : (
            <ul className="ml-4 space-y-1">
              {assigned.map(user => (
                <li key={user.id} className="font-medium">{user.full_name}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  )
}
